// web/src/routes/supplements/HorseDoses.jsx
// Every horse on this supplement, and how much each one gets.
//
// Taylor, reading "a horse can override this" on the product form: "I'm pretty
// sure I know what this means but I think it needs to be clarified a little
// bit. Meaning if I have a specific horse I've assigned a different amount of
// the supplement, correct? 1 vs 2 scoops per day"
//
// She had it right, and had to work it out — but the worse problem was that
// the sentence promised something with nowhere to do it. The API has taken a
// per-horse dose since the first day; no screen ever asked for one. A hint
// describing a capability that does not exist is a bug with good grammar.
//
// So this is the panel the hint was pointing at. Most horses get the usual
// amount and say so in grey; the ones that differ say their own number, and
// that contrast is the whole design — a page where every row shows a figure
// makes you read nine numbers to find the one that is unusual.
//
// Putting a horse ON the supplement lives here too, for the same reason it
// was missing: the endpoint has always existed and nothing asked. And it
// belongs next to the list of who already gets it, because that list is the
// answer to "is this horse on it already" — the question anybody is actually
// holding when they go to add one.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Panel, Empty } from '../../components/dashboard/Planner';
import Stepper from '../../components/ui/Stepper';
import { friendly, trim } from './SupplementDetail';

export default function HorseDoses({ supplementId, owners, doseUnit, defaultDailyDoses, canManage }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // horse id
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  // Every horse in the barn, so one can be picked. Only fetched once the
  // panel is actually used for adding — the roster is of no interest to
  // somebody who opened this page to read a dose.
  const { data: barn } = useQuery({
    queryKey: ['horses'],
    queryFn: () => api.get('/horses'),
    enabled: adding,
  });

  // Flattened out of the owners, because the question here is "which horses",
  // and whose shelf pays for them is the question one panel up.
  const horses = owners
    .flatMap((o) => o.horses.map((h) => ({ ...h, ownerName: o.name })))
    .sort((a, b) => a.name.localeCompare(b.name));

  const add = useMutation({
    mutationFn: ({ horseId, dailyDoses }) =>
      api.put(`/supplements/${supplementId}/horses/${horseId}`, { dailyDoses }),
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplement', supplementId] });
      qc.invalidateQueries({ queryKey: ['supplements'] });
      setAdding(false);
    },
    onError: (e) => setError(friendly(e)),
  });

  // Taking a horse off ENDS the assignment rather than deleting it — the
  // server dates it, so last month's consumption still has a reason. That is
  // also why this is "take off" and not a bin icon: a bin promises the record
  // goes away, and it does not.
  const remove = useMutation({
    mutationFn: (horseId) => api.del(`/supplements/${supplementId}/horses/${horseId}`),
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplement', supplementId] });
      qc.invalidateQueries({ queryKey: ['supplements'] });
      setEditing(null);
    },
    onError: (e) => setError(friendly(e)),
  });

  const save = useMutation({
    mutationFn: ({ horseId, dailyDoses }) =>
      api.put(`/supplements/${supplementId}/horses/${horseId}`, { dailyDoses }),
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplement', supplementId] });
      qc.invalidateQueries({ queryKey: ['supplements'] });
      setEditing(null);
    },
    onError: (e) => setError(friendly(e)),
  });

  const onIt = new Set(horses.map((h) => h.id));
  const available = (barn?.horses ?? []).filter((h) => !onIt.has(h.id));

  return (
    <Panel
      title="Who gets it"
      action={
        canManage && !adding ? (
          <button
            type="button"
            onClick={() => { setAdding(true); setEditing(null); setError(null); }}
            className="normal-case tracking-normal text-hunter hover:underline"
          >
            + Add a horse
          </button>
        ) : (
          horses.length || null
        )
      }
    >
      {horses.length === 0 ? (
        <Empty>No horses are on this yet.</Empty>
      ) : (
        <ul className="planner-rows">
          {horses.map((h) => (
            <li key={h.id} className="px-3 py-2">
              {editing === h.id ? (
                <DoseEditor
                  horse={h}
                  doseUnit={doseUnit}
                  defaultDailyDoses={defaultDailyDoses}
                  busy={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(dailyDoses) => save.mutate({ horseId: h.id, dailyDoses })}
                  onRemove={() => remove.mutate(h.id)}
                  removing={remove.isPending}
                />
              ) : (
                <div className="flex items-baseline gap-3 text-sm">
                  <span className="min-w-0 flex-1">
                    <Link to={`/horses/${h.id}`} className="font-medium hover:underline">
                      {h.name}
                    </Link>
                    <span className="block text-xs text-ink-400 truncate">
                      {[h.ownerName, h.stall && `stall ${h.stall}`].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <Amount horse={h} doseUnit={doseUnit} defaultDailyDoses={defaultDailyDoses} />
                  </span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => { setEditing(h.id); setError(null); }}
                      className="shrink-0 text-xs px-2 py-1 rounded border border-ink-200 text-ink-600 hover:bg-ink-50"
                    >
                      Change
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="px-3 py-3 border-t border-hunter/20 bg-hunter/5">
          <AddHorse
            available={available}
            loading={!barn}
            doseUnit={doseUnit}
            defaultDailyDoses={defaultDailyDoses}
            busy={add.isPending}
            onCancel={() => setAdding(false)}
            onAdd={(horseId, dailyDoses) => add.mutate({ horseId, dailyDoses })}
          />
        </div>
      )}

      {error && <p className="px-3 pb-2 text-sm text-rose-700">{error}</p>}

      {horses.length > 0 && defaultDailyDoses != null && (
        <p className="px-3 pb-2 text-[11px] text-ink-400">
          Horses without their own amount follow the usual {trim(defaultDailyDoses)}{' '}
          {doseUnit}{defaultDailyDoses === 1 ? '' : 's'} a day, and move with it if you change it.
        </p>
      )}
    </Panel>
  );
}

/**
 * What this horse gets, and whether anybody chose it.
 *
 * The distinction is the point: "2 scoops" set deliberately on this horse and
 * "2 scoops because that is the usual" behave differently the moment somebody
 * edits the product, and a reader deciding whether to touch it needs to know
 * which one they are looking at.
 */
function Amount({ horse: h, doseUnit, defaultDailyDoses }) {
  const own = h.dailyDoses;
  const n = own ?? defaultDailyDoses;
  if (n == null) {
    return <span className="text-xs text-amber-700">no amount set</span>;
  }
  return (
    <>
      <span className={'block tabular-nums leading-none ' + (own != null ? 'font-semibold' : 'text-ink-500')}>
        {trim(n)}
      </span>
      <span className="block text-[11px] text-ink-400">
        {own != null ? `${doseUnit}s a day` : 'the usual'}
      </span>
    </>
  );
}

function DoseEditor({ horse: h, doseUnit, defaultDailyDoses, busy, onCancel, onSave, onRemove, removing }) {
  const [confirming, setConfirming] = useState(false);
  const [amount, setAmount] = useState(String(h.dailyDoses ?? defaultDailyDoses ?? 1));
  const n = Number(amount);
  const valid = Number.isFinite(n) && n > 0;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{h.name}</p>
      <p className="text-xs text-ink-500">
        How many {doseUnit}s {h.name} gets a day.
      </p>
      {/* Halves are real — "3/4 cup" and "half a tube" are both in Westwood's
          own list — so the stepper moves in halves and the field still takes
          anything typed. */}
      <Stepper value={amount} onChange={setAmount} min={0} step={0.5} disabled={busy} label={`${doseUnit}s a day`} />
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" className="btn-primary" disabled={busy || !valid}
          onClick={() => onSave(n)}>
          {busy ? 'Saving…' : `${trim(n)} a day`}
        </button>
        {defaultDailyDoses != null && h.dailyDoses != null && (
          // Back to following the product, which is not the same as typing the
          // product's current number in: one tracks future changes, the other
          // freezes today's.
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => onSave(null)}>
            Use the usual
          </button>
        )}
        <button type="button" className="btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>

      {/* Two taps, and the second one says what it will and will not do. A
          horse coming off a supplement is a real event with a date on it, not
          a row being tidied away, and the history keeps it. */}
      {onRemove && (
        <div className="pt-1">
          {confirming ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 space-y-2">
              <p className="text-xs text-rose-800">
                Take {h.name} off this from today. What has already been fed and ordered stays in
                the history — it just stops counting toward anybody&apos;s shelf from now on.
              </p>
              <div className="flex gap-2">
                <button type="button" className="btn-primary bg-rose-700 hover:bg-rose-800"
                  disabled={removing} onClick={onRemove}>
                  {removing ? 'Taking off…' : `Take ${h.name} off`}
                </button>
                <button type="button" className="btn-secondary" disabled={removing}
                  onClick={() => setConfirming(false)}>Keep</button>
              </div>
            </div>
          ) : (
            <button type="button" className="text-xs text-ink-400 hover:text-rose-700 underline underline-offset-2"
              disabled={busy} onClick={() => setConfirming(true)}>
              Take {h.name} off this supplement
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Putting a horse on.
 *
 * The dose is asked for at the same time rather than afterwards, because the
 * question "which horse" and the question "how much" arrive together in
 * somebody's head — and leaving it blank would add a horse that eats an
 * unknown amount, which is the one state that makes the whole product
 * unpredictable.
 */
function AddHorse({ available, loading, doseUnit, defaultDailyDoses, busy, onCancel, onAdd }) {
  const [horseId, setHorseId] = useState('');
  const [amount, setAmount] = useState(String(defaultDailyDoses ?? 1));
  const n = Number(amount);
  const valid = !!horseId && Number.isFinite(n) && n > 0;
  const horse = available.find((h) => h.id === horseId);

  if (loading) return <p className="text-sm text-ink-400">Loading the horses…</p>;

  if (available.length === 0) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-500">Every horse in the barn is already on this.</p>
        <button type="button" className="btn-secondary" onClick={onCancel}>Close</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">Horse</span>
        <select className="input" value={horseId} disabled={busy}
          onChange={(e) => setHorseId(e.target.value)}>
          <option value="">Pick a horse…</option>
          {available.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}{h.stall ? ` · stall ${h.stall}` : ''}
            </option>
          ))}
        </select>
      </label>

      <div>
        <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
          {cap(doseUnit)}s a day
        </span>
        <Stepper value={amount} onChange={setAmount} min={0} step={0.5} disabled={busy}
          label={`${doseUnit}s a day`} />
        <p className="mt-1.5 text-xs text-ink-500">
          {defaultDailyDoses != null && n === defaultDailyDoses
            ? `The usual for this supplement. Change it on the product and ${horse?.name ?? 'this horse'} moves with it.`
            : `Just for ${horse?.name ?? 'this horse'}${defaultDailyDoses != null ? `, instead of the usual ${trim(defaultDailyDoses)}` : ''}.`}
        </p>
      </div>

      <div className="flex gap-2">
        <button type="button" className="btn-primary" disabled={busy || !valid}
          onClick={() => onAdd(horseId, defaultDailyDoses != null && n === defaultDailyDoses ? null : n)}>
          {busy ? 'Adding…' : horse ? `Put ${horse.name} on it` : 'Add'}
        </button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function cap(x) {
  return x ? x[0].toUpperCase() + x.slice(1) : x;
}
